/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useMemo, useEffect, BaseSyntheticEvent } from 'react';
import styled from 'styled-components';
import { ModalContainerDark, NewUpdateModal } from 'components/modal-provider/modal';
import { CloseIcon, ButtonContainer, CancelButton } from 'components/formula-entry/modals/_common';
import { IProductReleasesData } from 'store/add-new-update/types';
import ReactQuill from 'react-quill';
import StyledInput from 'components/input/text';
import colors from 'theme/colors';
import { EUpdateModalTitle } from 'lib/storage';
import Button from 'components/buttons/simple';
import './styles.scss';
import 'react-quill/dist/quill.snow.css';
import { TAddNewUpdateModalProps } from '.';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: ${colors.platinum};
`;

const HeaderContainer = styled.div`
  text-align: left;
  position: relative;
`;

export const SaveButton = styled(Button)`
  background-color: ${colors.yellow};
  color: ${colors.darkBorder};
  margin-left: 8px;
`;

const TitleContainer = styled.div`
  text-align: left;
  position: relative;
`;

export const Header = styled.div`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 4.75px;
`;

export const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 24px;
  color: ${colors.modalSubTitle};
  opacity: 60%;
`;

export const TitleLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin-bottom: 5px;
`;

const Spacer = styled.div`
  margin-top: 26px;
`;

const CustomToolbar = () => (
  <div id="toolbar">
    <select className="ql-header" defaultValue="4" style={{ width: 90 }}>
      <option value="3">Heading</option>
      <option value="4">Normal</option>
    </select>
    <span className="ql-formats">
      <button type="button" className="ql-bold" />
      <button type="button" className="ql-italic" />
      <button type="button" className="ql-underline" />
    </span>

    <span className="ql-formats">
      <button type="button" className="ql-list" value="ordered" />
      <button type="button" className="ql-list" value="bullet" />
    </span>
  </div>
);

interface IEditorProps {
  placeholder: string;
  saveEditorData: (editorHtml: string) => void;
  editorhtml: string;
  modalTitle: string;
  productReleaseObj: IProductReleasesData | { title: ''; description: ''; id: '' };
  setUpdateIds: (updateIds: string) => void;
}

function Editor(props: IEditorProps) {
  const formats = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet'];

  const modules = useMemo(
    () => ({
      toolbar: {
        container: '#toolbar',
      },
    }),
    []
  );
  const { placeholder, saveEditorData, editorhtml, modalTitle, productReleaseObj, setUpdateIds } = props;

  useEffect(() => {
    if (productReleaseObj && modalTitle === 'Edit Update') {
      saveEditorData(productReleaseObj.description);
      setUpdateIds(productReleaseObj.id);
    } else {
      saveEditorData('');
      setUpdateIds('');
    }
  }, [modalTitle, productReleaseObj, saveEditorData, setUpdateIds]);

  const handleChange = (html: string) => {
    saveEditorData(html);
  };

  return (
    <div className="text-editor">
      <CustomToolbar />
      <ReactQuill
        value={editorhtml}
        onChange={handleChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />
    </div>
  );
}

export default ({
  setAddNewUpdateModalIsOpen,
  isModalOpen,
  setPostUpdateModalIsOpen,
  saveEditorData,
  setPostUpdateConfirmButtonPressed,
  saveTitle,
  modalTitle,
  editorhtml,
  updateModalButtonTittle,
  updateModalSubTitle,
  productReleaseObj,
  setUpdateIds,
}: TAddNewUpdateModalProps) => {
  if (!isModalOpen) {
    return null;
  }

  const [title, setTitle] = useState(productReleaseObj ? productReleaseObj.title : '');

  const handleTitleChange = (e: BaseSyntheticEvent) => {
    setTitle(e.target.value);
  };

  const handleClick = () => {
    setPostUpdateModalIsOpen(true);
    setAddNewUpdateModalIsOpen(false, EUpdateModalTitle.EDIT_UPDATE);
    setPostUpdateConfirmButtonPressed(false);
    saveTitle(title);
  };
  return (
    <>
      <ModalContainerDark data-testid="text-editor-modal">
        <NewUpdateModal>
          <Container>
            <HeaderContainer>
              <Header>{modalTitle}</Header>
              <Label>{updateModalSubTitle}</Label>
              <CloseIcon onClick={() => setAddNewUpdateModalIsOpen(false, EUpdateModalTitle.EDIT_UPDATE)} />
            </HeaderContainer>
            <TitleContainer>
              <TitleLabel>Update Title</TitleLabel>
              <StyledInput
                width="406px"
                placeholder="Enter title for the update"
                value={title}
                onChange={(e: BaseSyntheticEvent) => {
                  setTitle(e.target.value);
                  handleTitleChange(e);
                }}
              />
              <Spacer />
              <TitleLabel>Update Description</TitleLabel>
              <div className="editor">
                <Editor
                  placeholder="Enter text here"
                  editorhtml={editorhtml}
                  saveEditorData={saveEditorData}
                  modalTitle={modalTitle}
                  productReleaseObj={productReleaseObj}
                  setUpdateIds={setUpdateIds}
                />
              </div>
            </TitleContainer>
            <ButtonContainer>
              <CancelButton onClick={() => setAddNewUpdateModalIsOpen(false, EUpdateModalTitle.EDIT_UPDATE)}>
                Cancel
              </CancelButton>
              <SaveButton
                disabled={title === '' || editorhtml === ''}
                data-testid="post-update-button"
                onClick={handleClick}
              >
                {updateModalButtonTittle}
              </SaveButton>
            </ButtonContainer>
          </Container>
        </NewUpdateModal>
      </ModalContainerDark>
    </>
  );
};
